import { useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';

const FAQ = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('All');
    const [openItems, setOpenItems] = useState({});

    const categories = ['All', 'Process', 'Pricing & Payments', 'Timelines', 'Technical', 'Policies'];

    const faqData = [
        {
            category: 'Process',
            question: 'How do I start a project with Projekto?',
            answer: "Starting is simple. Click the 'Get Started' button, fill out our comprehensive project form with your requirements, and complete the partial payment to confirm your order. Our team will review and start work immediately."
        },
        {
            category: 'Process',
            question: 'What happens after I submit the form?',
            answer: "Once submitted and payment is confirmed, you'll receive an email confirmation. We assign a lead developer to your project who will review your requirements. If we need any clarifications, we'll reach out via email. Otherwise, development begins right away!"
        },
        {
            category: 'Pricing & Payments',
            question: 'Are there any recurring subscription fees?',
            answer: "No. Projekto operates on a per-project basis. You only pay for the specific deliverables you order. There are no monthly fees or hidden costs."
        },
        {
            category: 'Pricing & Payments',
            question: 'Why do I need to pay a partial amount upfront?',
            answer: "The partial payment serves as a commitment and allows us to allocate resources and high-quality developers to your project immediately. The remaining balance is handled as per the agreement during the handover."
        },
        {
            category: 'Timelines',
            question: 'How long does a typical project take?',
            answer: "Most projects are completed within 2 to 5 business days. Complex custom requirements might take longer. We always provide an estimated timeline once we've reviewed your specific project details."
        },
        {
            category: 'Timelines',
            question: 'Can I request an urgent delivery?',
            answer: "Yes, we offer priority handling for urgent projects. You can specify your deadline in the 'Additional Information' section, and we will do our best to accommodate it."
        },
        {
            category: 'Technical',
            question: 'What technologies do you use for development?',
            answer: "We specialize in modern web technologies including React.js, Node.js, Express, and high-performance Vanilla CSS. We focus on building fast, responsive, and SEO-friendly applications."
        },
        {
            category: 'Technical',
            question: 'Will my website be mobile-friendly?',
            answer: "Absolutely. Mobile-first responsive design is our standard. Every project we deliver is tested across multiple screen sizes to ensure a perfect experience on mobile, tablet, and desktop."
        },
        {
            category: 'Policies',
            question: 'What is your refund policy?',
            answer: "Refunds are available if work has not yet started on your project. Once development begins, we offer free revisions to ensure you're satisfied with the result rather than a refund."
        },
        {
            category: 'Policies',
            question: 'How many revisions can I request?',
            answer: "We typically offer 2 to 3 rounds of major revisions. Minor tweaks are usually handled without count to ensure your project matches your vision perfectly."
        }
    ];

    const toggleItem = (index) => {
        setOpenItems(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const filteredFaqs = faqData.filter(faq => {
        const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = activeCategory === 'All' || faq.category === activeCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="bg-dark-bg min-h-screen text-white flex flex-col">
            {/* Header/Nav */}
            <Header variant="landing" />

            {/* Hero Section */}
            <main className="flex-1 max-w-4xl mx-auto px-6 py-16 w-full">
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">How can we help?</h1>
                    <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                        Find answers to common questions about our process, pricing, and technical expertise.
                    </p>
                </div>

                {/* Search Bar */}
                <div className="relative mb-12">
                    <span className="material-symbols-outlined absolute left-5 top-1/2 -translate-y-1/2 text-gray-500">search</span>
                    <input
                        type="text"
                        placeholder="Search for questions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-dark-card border border-border-gray rounded-2xl py-5 pl-14 pr-6 text-white focus:outline-none focus:border-white transition-all text-lg placeholder:text-gray-600 shadow-xl"
                    />
                </div>

                {/* Category Tabs */}
                <div className="flex flex-wrap justify-center gap-2 mb-12">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setActiveCategory(cat)}
                            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${activeCategory === cat
                                ? 'bg-white text-black'
                                : 'bg-dark-card text-gray-400 border border-border-gray hover:border-gray-600'
                                }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {/* FAQ List */}
                <div className="space-y-4">
                    {filteredFaqs.length > 0 ? (
                        filteredFaqs.map((faq, index) => (
                            <div
                                key={index}
                                className="bg-dark-card border border-border-gray rounded-2xl overflow-hidden transition-all hover:border-gray-700"
                            >
                                <button
                                    onClick={() => toggleItem(index)}
                                    className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
                                >
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs font-bold text-blue-400 uppercase tracking-widest">{faq.category}</span>
                                        <span className="text-lg font-bold pr-8">{faq.question}</span>
                                    </div>
                                    <span className={`material-symbols-outlined transition-transform duration-300 text-gray-500 ${openItems[index] ? 'rotate-180' : ''}`}>
                                        expand_more
                                    </span>
                                </button>
                                <div
                                    className={`px-6 transition-all duration-300 ease-in-out overflow-hidden ${openItems[index] ? 'max-h-96 pb-6 opacity-100' : 'max-h-0 opacity-0'
                                        }`}
                                >
                                    <p className="text-gray-400 leading-relaxed border-t border-border-gray pt-4">
                                        {faq.answer}
                                    </p>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-20 bg-dark-card border border-dashed border-border-gray rounded-2xl">
                            <span className="material-symbols-outlined text-4xl text-gray-600 mb-4">search_off</span>
                            <p className="text-gray-500">No matching questions found.</p>
                            <button
                                onClick={() => { setSearchTerm(''); setActiveCategory('All'); }}
                                className="text-blue-400 mt-2 hover:underline"
                            >
                                Clear filters
                            </button>
                        </div>
                    )}
                </div>

                {/* Still Have Questions */}
                <div className="mt-20 p-8 rounded-3xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-white/5 text-center">
                    <h3 className="text-2xl font-bold mb-3">Still have questions?</h3>
                    <p className="text-gray-400 mb-8">Can't find the answer you're looking for? Please chat to our friendly team.</p>
                    <a
                        href="mailto:projecktocare@gmail.com"
                        className="bg-white text-black px-8 py-3 rounded-full font-bold hover:bg-gray-200 transition-all inline-flex items-center gap-2 active:scale-95"
                    >
                        <span className="material-symbols-outlined text-xl">mail</span>
                        Contact Support
                    </a>
                </div>
            </main>

            <Footer variant="dark-form" />
        </div>
    );
};

export default FAQ;
